import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Activity, Eye, EyeOff, Building2, Mail, HelpCircle, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    agencyCode: "",
    email: "",
    password: "",
  });

  // Forgot agency code modal state
  const [showForgotAgencyModal, setShowForgotAgencyModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [agencyCodeResult, setAgencyCodeResult] = useState(null);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleForgotAgencyCode = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setForgotLoading(true);
    setAgencyCodeResult(null);
    
    try {
      const response = await fetch(`${API}/auth/forgot-agency-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.agency_code) {
        setAgencyCodeResult({
          success: true,
          agencyCode: data.agency_code,
          agencyName: data.agency_name,
        });
      } else {
        setAgencyCodeResult({
          success: false,
          error: data.detail || "No account found with this email address",
        });
      }
    } catch (error) {
      setAgencyCodeResult({
        success: false,
        error: "Unable to look up agency code. Please try again.",
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleUseAgencyCode = () => {
    if (agencyCodeResult?.agencyCode) {
      setFormData(prev => ({
        ...prev,
        agencyCode: agencyCodeResult.agencyCode,
        email: forgotEmail,
      }));
      setShowForgotAgencyModal(false);
      setForgotEmail("");
      setAgencyCodeResult(null);
      toast.success("Agency code filled in!");
    }
  };

  const closeForgotModal = () => {
    setShowForgotAgencyModal(false);
    setForgotEmail("");
    setAgencyCodeResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password, formData.agencyCode);

    if (result.success) {
      // Check if subscription is valid
      if (result.subscriptionValid === false) {
        // Redirect to subscription page - must subscribe before accessing platform
        navigate("/subscribe");
      } else {
        toast.success("Welcome back!");
        navigate("/dashboard");
      }
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img 
              src="/carepilot-logo.png" 
              alt="CarePilot" 
              className="h-40 w-auto"
            />
          </Link>

          <h1 className="text-3xl font-bold text-slate-900">Welcome back</h1>
          <p className="mt-2 text-slate-600">
            Sign in to access your agency dashboard
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {/* Agency Code */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="agencyCode">Agency Code</Label>
                <button
                  type="button"
                  onClick={() => setShowForgotAgencyModal(true)}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                  data-testid="forgot-agency-code-link"
                >
                  <HelpCircle className="w-3 h-3" />
                  Forgot agency code?
                </button>
              </div>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="agencyCode"
                  name="agencyCode"
                  type="text"
                  placeholder="Enter your agency code"
                  value={formData.agencyCode}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="login-agency-code"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="login-email"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner"></span>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-slate-600">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline">
              Register your agency
            </Link>
          </p>
        </div>
      </div>

      {/* Forgot Agency Code Modal */}
      <Dialog open={showForgotAgencyModal} onOpenChange={closeForgotModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Forgot Agency Code?
            </DialogTitle>
            <DialogDescription>
              Enter your email address and we'll look up your agency code.
            </DialogDescription>
          </DialogHeader>
          
          {!agencyCodeResult ? (
            <form onSubmit={handleForgotAgencyCode} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="forgotEmail">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="forgotEmail"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="forgot-agency-email"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForgotModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="flex-1"
                  data-testid="forgot-agency-submit"
                >
                  {forgotLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Looking up...
                    </span>
                  ) : (
                    "Look Up Code"
                  )}
                </Button>
              </div>
            </form>
          ) : agencyCodeResult.success ? (
            <div className="space-y-4 mt-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800">Agency Found!</p>
                    <p className="text-sm text-emerald-700 mt-1">
                      Agency: <span className="font-semibold">{agencyCodeResult.agencyName}</span>
                    </p>
                    <p className="text-sm text-emerald-700">
                      Code: <span className="font-mono font-bold text-lg">{agencyCodeResult.agencyCode}</span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForgotModal}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={handleUseAgencyCode}
                  className="flex-1"
                  data-testid="use-agency-code-btn"
                >
                  Use This Code
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{agencyCodeResult.error}</p>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForgotModal}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={() => setAgencyCodeResult(null)}
                  className="flex-1"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 relative bg-primary">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20"></div>
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white text-center">
          <h2 className="text-4xl font-bold max-w-md">
            Streamline Your Home Care Operations
          </h2>
          <p className="mt-4 text-lg text-white/80 max-w-md">
            Manage clients, employees, scheduling, and billing all in one powerful
            platform.
          </p>
        </div>
      </div>
    </div>
  );
}
